// Settings › Payer rules — bigger reference mock (00-ds-09 §3, D148, lists at
// scale). ~120 deterministic learned rules so the reference table can search
// and page (50/page). Read-only; learned server-side from manuals/portals/
// claims, never typed in (§8).

import { generate, pick } from '@/components/lists';

export interface PayerRule {
  id: string;
  payer: string;
  rule: string;
  source: string;
}

const PAYER_NAMES = [
  'Aetna', 'Delta Dental', 'Cigna', 'MetLife', 'UnitedHealthcare', 'Guardian',
  'Sun Life', 'BCBS of Texas', 'Humana', 'Ameritas', 'Principal', 'Lincoln Financial',
  'Careington', 'DenteMax', 'GEHA', 'Anthem', 'Dominion National', 'UnitedConcordia',
] as const;

const RULES = [
  'A build-up billed with a crown needs a periodontal chart from the last 12 months.',
  'Takes appeals by fax and certified mail, not email.',
  'Pays about 30 days after a clean claim; appeals accepted on the provider portal.',
  'Scaling and root planing needs full-mouth probing dated within 60 days.',
  'A narrative is required for any extraction billed with bone graft.',
  'Posterior composites are downgraded to amalgam on the allowed fee.',
  'Only one periapical plus bitewings per date of service without a narrative.',
  'Night guards need a diagnosis of bruxism and a photo of wear.',
  'Crowns under five years old need a pre-op radiograph to be remade.',
  'Frequency on adult prophy is two per benefit year, six months apart.',
  'Pre-auth is required above $700 or it is held as patient responsibility.',
  'Appeals must be filed within 180 days of the original remittance.',
  'Fluoride is covered to age 16 only; older patients are billed out.',
  'A second molar root canal needs a working-length radiograph attached.',
  'Occlusal guards are a once-every-three-years benefit.',
] as const;

const SOURCES = [
  'Payer manual · Mar 2026',
  'Payer manual · Jan 2026',
  'Payer portal',
  'Your claims',
  'Payer manual · Oct 2025',
  'Phone call with the payer · Sep 2026',
] as const;

export const PAYER_RULES: PayerRule[] = generate<PayerRule>(120, 0xb3a7, (r, i) => ({
  id: `pr-${String(i)}`,
  payer: pick(r, PAYER_NAMES),
  rule: pick(r, RULES),
  source: pick(r, SOURCES),
})).sort((a, b) => (a.payer === b.payer ? a.rule.localeCompare(b.rule) : a.payer.localeCompare(b.payer)));

/** Distinct payers represented, for the section meta line. */
export const PAYER_RULE_PAYER_COUNT = new Set(PAYER_RULES.map((x) => x.payer)).size;

// ---------------------------------------------------------------------------
// One payer list, one id scheme (D168, D202). `payerId` is the slug of the name
// in the one payer list: `aetna`, `delta-dental-tx`, `bcbs-of-texas`. A plan is a
// query, never part of the id: /settings/payers/aetna?plan=ppo.
// ---------------------------------------------------------------------------

function slugOf(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export interface PayerInfo {
  id: string;
  name: string;
  /** The plans ELVA keeps separate rules for, e.g. PPO, HMO. */
  plans: { id: string; label: string }[];
}

const PLAN_SETS: Record<string, string[]> = {
  cigna: ['DPPO', 'DHMO'],
  'delta-dental-tx': ['PPO', 'Premier'],
  metlife: ['PDP Plus', 'Preferred'],
  guardian: ['PPO', 'DentalGuard'],
  'bcbs-of-texas': ['PPO', 'HMO'],
};

/** "Delta Dental" is six payers that don't share a system; this practice's is Delta Dental of Texas (D168). */
const ID_OVERRIDES: Record<string, string> = { 'Delta Dental': 'delta-dental-tx' };

export const PAYER_LIST: PayerInfo[] = PAYER_NAMES.map((name) => {
  const id = ID_OVERRIDES[name] ?? slugOf(name);
  const labels = PLAN_SETS[id] ?? ['PPO', 'HMO'];
  return { id, name, plans: labels.map((label) => ({ id: slugOf(label), label })) };
});

/** Older or looser ids that still reach the same payer (Live Check says delta-dental-tx). */
const PAYER_ALIASES: Record<string, string> = {
  'delta-dental': 'delta-dental-tx',
  'delta-dental-of-texas': 'delta-dental-tx',
  'bcbs-tx': 'bcbs-of-texas',
};

export function findPayer(payerId: string | undefined): PayerInfo | null {
  if (!payerId) return null;
  const key = PAYER_ALIASES[payerId] ?? payerId;
  return PAYER_LIST.find((p) => p.id === key) ?? null;
}

/** A payer's rules, each filed under one of its plans, or null = every plan. Stable per rule id. */
export interface PayerRuleInPlan extends PayerRule {
  plan: string | null;
}

export function rulesForPayer(payer: PayerInfo): PayerRuleInPlan[] {
  const mine = PAYER_RULES.filter((r) => r.payer === payer.name);
  const n = payer.plans.length + 1;
  return mine.map((r, i) => ({ ...r, plan: i % n === 0 ? null : (payer.plans[(i % n) - 1]?.id ?? null) }));
}

/**
 * Where a payer name from anywhere in the app ("Aetna PPO", "Cigna DPPO",
 * "Delta Dental of Texas · PPO · in-network", "Guardian") opens in Settings ›
 * Payer rules: the payer's page, with the plan in the query when the name
 * carries one. Falls back to the list when the payer isn't in the one list.
 */
export function payerPathFor(name: string): string {
  const parts = name.split('·').map((x) => x.trim());
  const low = (parts[0] ?? name).toLowerCase();
  const hit = PAYER_LIST
    .filter((p) => low === p.name.toLowerCase() || low.startsWith(`${p.name.toLowerCase()} `))
    .sort((a, b) => b.name.length - a.name.length)[0];
  if (!hit) return '/settings/payers';
  const rest = low.slice(hit.name.length).trim();
  const planGuess = rest !== '' && !rest.startsWith('of ') ? rest : (parts[1] ?? '').toLowerCase();
  const plan = hit.plans.find((pl) => pl.id === slugOf(planGuess) || pl.label.toLowerCase() === planGuess);
  return plan ? `/settings/payers/${hit.id}?plan=${plan.id}` : `/settings/payers/${hit.id}`;
}
